// User identification constants and helpers
export const USER_ID_TYPES = {
  FIREBASE_UID: 'firebaseUid',  // The Firebase-generated document ID
  APP_ID: 'appId',              // Our internal vanity ID (e.g., UID001)
};

// Helper function to get current user details - using consistent naming
export const getCurrentUserDetails = (currentUser) => {  // matches useAuth() naming
  if (!currentUser) return null;
  
  return {
    firebaseUid: currentUser.uid,        // Firebase document ID
    appId: currentUser.appID,            // Our internal ID (e.g., UID001)
    email: currentUser.email,
    displayName: currentUser.displayName,
    isAuthenticated: !!currentUser,
  };
};

// Helper to get the correct ID based on context - using consistent naming
export const getUserId = (currentUser, idType = USER_ID_TYPES.APP_ID) => {  // matches useAuth() naming
  if (!currentUser) return null;
  
  switch (idType) {
    case USER_ID_TYPES.FIREBASE_UID:
      return currentUser.uid;
    case USER_ID_TYPES.APP_ID:
      return currentUser.appID;
    default:
      console.error('Invalid ID type requested');
      return null;
  }
};

// Helper to construct mentor details path - using consistent naming
export const getMentorDetailsPath = (discipleId, currentUser) => {  // matches useAuth() naming
  const appId = getUserId(currentUser, USER_ID_TYPES.APP_ID);
  if (!appId || !discipleId) return null;
  return `disciples/${discipleId}/mentorDetails/${appId}`;
};

// Export bookOrder to be used in other components like ViewVerses.js
export const bookOrder = [
    "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy", "Joshua", "Judges", "Ruth",
    "1 Samuel", "2 Samuel", "1 Kings", "2 Kings", "1 Chronicles", "2 Chronicles", "Ezra",
    "Nehemiah", "Esther", "Job", "Psalms", "Proverbs", "Ecclesiastes", "Song of Solomon",
    "Isaiah", "Jeremiah", "Lamentations", "Ezekiel", "Daniel", "Hosea", "Joel", "Amos",
    "Obadiah", "Jonah", "Micah", "Nahum", "Habakkuk", "Zephaniah", "Haggai", "Zechariah", "Malachi",
    "Matthew", "Mark", "Luke", "John", "Acts", "Romans", "1 Corinthians", "2 Corinthians", "Galatians",
    "Ephesians", "Philippians", "Colossians", "1 Thessalonians", "2 Thessalonians", "1 Timothy", "2 Timothy",
    "Titus", "Philemon", "Hebrews", "James", "1 Peter", "2 Peter", "1 John", "2 John", "3 John", "Jude", "Revelation"
  ];
  
// Common words to filter out in contextual matching
export const commonWords = [
    'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'i',
    'it', 'for', 'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at',
    'this', 'but', 'his', 'by', 'from', 'they', 'we', 'say', 'her', 'she',
    'or', 'an', 'my', 'one', 'all', 'would', 'there', 'their', 'what',
    'can', 'who', 'get', 'if', 'would', 'her', 'all', 'my', 'make', 'about',
    'know', 'will', 'up', 'one', 'time', 'there', 'year', 'so', 'think', 'when',
    'which', 'them', 'some', 'me', 'people', 'take', 'out', 'into', 'just', 'see',
    'him', 'your', 'come', 'could', 'now', 'than', 'like', 'other', 'how', 'then',
    'its', 'our', 'two', 'more', 'these', 'want', 'way', 'look', 'first', 'also',
    'new', 'because', 'day', 'use', 'no', 'man', 'find', 'here', 'thing', 'give'
  ];
  
  // Theme/keyword mappings based on our growth areas and development topics
  export const THEME_MAPPINGS = {
    EMOTIONAL: {
      tags: [
        // Original tags
        'anxiety', 'worry', 'stress', 'fear', 'peace', 'joy', 'depression', 'emotions', 'feelings',
        'struggling', 'condemnation', 'broke up', 'treats', 'relationship', 'girlfriend',
        // Additional tags 
        'cant sleep', 'overthinking', 'nervous', 'freaking out', 'panic', 'scared', 'upset',
        'down', 'blue', 'rough day', 'rough time', 'hard time', 'tired of', 'fed up',
        'cant handle', 'overwhelmed', 'burnt out', 'exhausted', 'drained', 'stuck',
        'lost', 'confused', 'dont know what to do', 'need help', 'blamed', 'blame', 'abuse', 'abused', 'yelled', 'yelling',
        // NEW everyday language to add
        'feeling down', 'bad mood', 'crying', 'tears', 'heartbroken', 'angry',
        'mad at', 'frustrated', 'annoyed', 'irritated', 'alone', 'lonely',
        'isolated', 'misunderstood', 'heavy heart', 'weight on shoulders',
        'mind racing', 'restless', 'on edge', 'cant focus', 'distracted',
        'emotional rollercoaster', 'mood swings', 'feeling empty', 'numb'
      ],
      categories: [
        // Keep all existing categories
        'Mental Health', 'Emotional Wellness', 'Personal Growth',
        'Anxiety Management', 'Depression Support', 'Stress Relief',
        'Emotional Intelligence', 'Self-Care', 'Mental Peace',
        // Add new categories
        'Mood Support', 'Emotional Healing', 'Mental Wellness',
        'Psychological Health', 'Emotional Balance', 'Inner Peace'
      ],
      growthArea: 'Emotional Development',
      devTopics: ['Mental and Emotional Health', 'Identity in Christ and Intrinsic Value']
    },
    SPIRITUAL: {
      tags: [
        // Original tags
        'prayer', 'faith', 'worship', 'trust', 'belief', 'holy spirit', 'salvation',
        // Additional tags
        'bible', 'scripture', 'word of god', 'quiet time', 'devotional', 'church',
        'spiritual growth', 'discipleship', 'testimony', 'witness', 'gospel', 'jesus',
        'god', 'christ', 'holy spirit', 'trinity', 'sin', 'repentance', 'forgiveness',
        'grace', 'mercy', 'redemption', 'salvation', 'heaven', 'eternal life', 'faith walk',
        'spiritual warfare', 'temptation', 'conviction',
        // NEW everyday language
        'went to church', 'read my bible', 'prayed today', 'small group',
        'feeling far from god', 'need guidance', 'seeking answers', 'questioning faith',
        'want to grow', 'learning about god', 'trying to trust god', 'doubting',
        'worship music', 'praise song', 'sermon', 'message', 'preaching',
        'fellowship', 'community group', 'bible app', 'devotional app',
        'spiritual attack', 'testing', 'trials', 'blessed', 'miracle'
      ],
      categories: ['Prayer', 'Faith', 'Worship', 'Spiritual Disciplines'],
      growthArea: 'Spiritual Development',
      devTopics: [
        'Personal Faith and Relationship with God',
        'Prayer and Spiritual Disciplines',
        'Biblical Understanding and Application',
        'Spiritual Warfare',
        'Identity in Christ and Intrinsic Value'
      ]
    },
    RELATIONAL: {
      tags: [
        // Original tags
        'relationships', 'family', 'marriage', 'friendship', 'forgiveness', 'love', 'community',
        'sister', 'brother', 'girlfriend', 'boyfriend', 'treats', 'broke up', 'conflict',
        // Additional tags
        'parents', 'mom', 'dad', 'sibling', 'dating', 'boundaries', 'trust issues',
        'communication', 'argument', 'fight', 'reconciliation', 'friends', 'coworkers',
        'neighbors', 'church family', 'mentor', 'discipleship', 'fellowship', 'unity',
        'division', 'gossip', 'betrayal', 'loyalty', 'commitment', 'dating relationship',
        'breakup', 'divorce', 'marriage problems', 'marriage advice', 'marriage counseling',
        // NEW everyday language
        'hanging out', 'met someone', 'talking to', 'getting along', 'not speaking',
        'miss them', 'catching up', 'reach out', 'text them', 'call them',
        'making friends', 'lonely', 'need community', 'social life', 'relationship advice',
        'dating app', 'single', 'married life', 'family drama', 'in-laws',
        'work friends', 'church friends', 'small group', 'life group', 'mentor', 'fight', 
        'tiff', 'roommate', 'girl drama', 'salty', 'bitter', 'upset'
      ],
      categories: ['Relationships', 'Family', 'Marriage', 'Community'],
      growthArea: 'Relational Development',
      devTopics: [
        'Relationships and Community',
        'Marriage and Family',
        'Conflict Resolution',
        'Communication and Listening'
      ]
    },
    VOCATIONAL: {
      tags: [
        // Original tags
        'work', 'career', 'purpose', 'calling', 'stewardship', 'leadership',
        // Additional tags
        'job', 'business', 'profession', 'workplace', 'colleagues', 'boss', 'employee',
        'promotion', 'interview', 'resume', 'career change', 'unemployment', 'finances',
        'money', 'budget', 'debt', 'investment', 'giving', 'tithing', 'generosity', 'finance', 
        'time management', 'priorities', 'goals', 'planning', 'decision making', 'bank', 'saving', 'banking',
        'wisdom', 'guidance', 'direction', 'homework', 'school', 'study', 'assignment', 
        'project', 'class', 'test', 'exam', 'quiz', 'course', 'skipped class', 'skipping', 'failing',
        'late', 'on time', 'on schedule', 'time management', 'priorities', 'goals', 'planning', 'decision making',
        'wisdom', 'guidance', 'direction', 'financial', 'money', 'budget', 'debt', 'investment', 'giving', 'tithing', 'generosity', 'finance', 
        // NEW everyday language
        'new job', 'job hunting', 'applied for', 'interview prep', 'networking',
        'side hustle', 'working late', 'overtime', 'work stress', 'toxic workplace',
        'boss problems', 'coworker drama', 'salary', 'raise', 'promotion',
        'career path', 'job satisfaction', 'work life balance', 'burnout',
        'quit my job', 'job offer', 'career advice', 'resume help', 'linkedin'
      ],
      categories: ['Work', 'Leadership', 'Stewardship'],
      growthArea: 'Vocational Development',
      devTopics: [
        'Work and Stewardship',
        'Time Management and Priorities',
        'Leadership Development',
        'Finances - Handling Money Gods Way'
      ]
    },
    PHYSICAL: {
      tags: [
        // Original tags
        'health', 'body', 'strength', 'rest', 'self-control',
        // Additional tags
        'exercise', 'fitness', 'nutrition', 'diet', 'sleep', 'stress management',
        // Add more workout-specific terms
        'workout', 'gym', 'working out', 'exercise more', 'need to exercise',
        'get in shape', 'get fit', 'fitness goals', 'training', 'cardio',
        'weights', 'strength training', 'physical activity', 'active',
        'health goals', 'healthy lifestyle', 'physical health', 'wellness',
        'workout plan', 'fitness routine', 'exercise plan', 'staying fit',
        'get healthy', 'be more active', 'physical fitness'
      ],
      categories: [
        'Physical Health', 'Self-Control', 'Wellness',
        'Exercise', 'Fitness', 'Physical Development',
        'Body Stewardship', 'Health Management'
      ],
      growthArea: 'Physical Development',
      devTopics: [
        'Stewardship of the Body',
        'Physical Health and Wellness',
        'Self-Control and Discipline'
      ]
    },
    MISSIONAL: {
      tags: [
        // Original tags
        'evangelism', 'missions', 'outreach', 'witness', 'service', 'ministry',
        // Additional tags
        'sharing faith', 'testimony', 'gospel', 'missionary', 'volunteer', 'serve',
        'helping others', 'charity', 'nonprofit', 'community service', 'teaching',
        'preaching', 'discipleship', 'mentoring', 'coaching', 'leading others',
        'church planting', 'cross-cultural', 'international', 'local missions',
        'global missions', 'unreached', 'kingdom work', 'great commission',
        'evangelistic', 'mission field',
        // NEW everyday language
        'shared jesus', 'talked about god', 'told my story', 'my testimony',
        'helped at church', 'volunteering', 'serving others', 'food bank',
        'homeless shelter', 'mission trip', 'outreach event', 'community event',
        'teaching sunday school', 'youth group', 'kids ministry', 'leading group',
        'mentoring someone', 'discipling', 'coffee with friend', 'praying together',
        'sharing the gospel', 'answered questions', 'faith conversation',
        'church volunteer', 'helping neighbor', 'showing gods love', 'shared his faith',
        'witness', 'testimony', 'gospel', 'witnessing', 'testifying',
        'witnessed', 'testified'
      ],
      categories: [
        // Existing categories
        'Evangelism', 'Missions', 'Ministry',
        // New categories
        'Local Outreach', 'Global Missions', 'Community Service',
        'Discipleship', 'Leadership', 'Teaching Ministry',
        'Cross-Cultural Ministry', 'Volunteer Work', 'Kingdom Impact'
      ],
      growthArea: 'Missional Development',
      devTopics: [
        'Evangelism and Sharing Your Faith',
        'Discipleship and Mentoring Others',
        'Cross-Cultural Ministry',
        'Service and Outreach'
      ]
    },
    'Holistic Personal Growth': {
      tags: [
        'growth', 'development', 'maturity', 'wisdom', 'balance', 'wholeness',
        'transformation', 'sanctification', 'discipleship', 'character',
        'integrity', 'values', 'purpose', 'identity', 'calling',
        'life goals', 'personal growth', 'spiritual growth', 'life change',
        'growing in faith', 'growing in christ', 'spiritual maturity',
        'christian living', 'daily walk', 'walk with god', 'following jesus'
      ],
      categories: [
        'Personal Development', 'Spiritual Growth', 'Life Purpose',
        'Character Formation', 'Christian Living', 'Discipleship',
        'Spiritual Maturity', 'Life Balance', 'Wholistic Growth'
      ],
      growthArea: 'Holistic Personal Growth',
      devTopics: [
        'Identity in Christ and Intrinsic Value',
        'Personal Faith and Relationship with God',
        'Biblical Understanding and Application',
        'Self-Control and Discipline',
        'Time Management and Priorities'
      ]
    },
    MENTAL: {
      tags: [
        'mind', 'thoughts', 'thinking', 'focus', 'clarity', 'wisdom', 'understanding', 'learning', 'knowledge', 'study',
        'mental health', 'mental strength', 'mental clarity', 'renewing the mind', 'discernment', 'insight', 'intellect',
        'reason', 'logic', 'decision making', 'problem solving', 'critical thinking', 'meditation', 'reflection',
        'concentration', 'attention', 'awareness', 'mindfulness', 'perspective', 'mental discipline', 'mental growth',
        'cognitive', 'brain', 'memory', 'remembrance', 'remind', 'reminded', 'reminding', 'remembrance',
        'learning new things', 'studying', 'education', 'school', 'class', 'lesson', 'teacher', 'student',
        'wisdom literature', 'proverbs', 'psalms', 'teach', 'teaching', 'instruct', 'instruction', 'guide', 'guidance'
      ],
      categories: [
        'Mental Health', 'Wisdom', 'Learning', 'Knowledge', 'Understanding', 'Focus', 'Mindfulness', 'Cognitive Growth',
        'Education', 'Reflection', 'Discernment', 'Insight', 'Mental Strength', 'Mental Clarity', 'Mental Discipline'
      ],
      growthArea: 'Mental Development',
      devTopics: [
        'Mental and Emotional Health', 'Understanding the Bible', 'Wisdom Literature', 'Learning and Growth', 'Discernment',
        'Critical Thinking', 'Decision Making', 'Reflection and Meditation'
      ]
    }
  };

export const GROWTH_AREA_COLORS = {
  'Spiritual Development': {
    bg: 'rgba(255, 99, 132, 0.5)',
    border: 'rgba(255, 99, 132, 1)',
    tailwindBg: 'bg-red-50',
    tailwindText: 'text-red-700',
    tailwindBorder: 'border-red-100'
  },
  'Holistic Personal Growth': {
    bg: 'rgba(147, 197, 253, 0.5)',
    border: 'rgba(147, 197, 253, 1)',
    tailwindBg: 'bg-blue-50',
    tailwindText: 'text-blue-400',
    tailwindBorder: 'border-blue-100'
  },
  'Relational Development': {
    bg: 'rgba(255, 206, 86, 0.5)',
    border: 'rgba(255, 206, 86, 1)',
    tailwindBg: 'bg-yellow-50',
    tailwindText: 'text-yellow-700',
    tailwindBorder: 'border-yellow-100'
  },
  'Physical Development': {
    bg: 'rgba(75, 192, 192, 0.5)',
    border: 'rgba(75, 192, 192, 1)',
    tailwindBg: 'bg-emerald-50',
    tailwindText: 'text-emerald-700',
    tailwindBorder: 'border-emerald-100'
  },
  'Vocational Development': {
    bg: 'rgba(153, 102, 255, 0.5)',
    border: 'rgba(153, 102, 255, 1)',
    tailwindBg: 'bg-purple-50',
    tailwindText: 'text-purple-700',
    tailwindBorder: 'border-purple-100'
  },
  'Missional Development': {
    bg: 'rgba(255, 159, 64, 0.5)',
    border: 'rgba(255, 159, 64, 1)',
    tailwindBg: 'bg-orange-50',
    tailwindText: 'text-orange-700',
    tailwindBorder: 'border-orange-100'
  },
  'Emotional Development': {
    bg: 'rgba(199, 199, 199, 0.5)',
    border: 'rgba(199, 199, 199, 1)',
    tailwindBg: 'bg-indigo-50',
    tailwindText: 'text-indigo-700',
    tailwindBorder: 'border-indigo-100'
  },
  'Mental Development': {
    bg: 'rgba(100, 181, 246, 0.5)', // light blue
    border: 'rgba(100, 181, 246, 1)',
    tailwindBg: 'bg-blue-100',
    tailwindText: 'text-blue-700',
    tailwindBorder: 'border-blue-200'
  }
};

export const GROWTH_AREAS = [
  'Spiritual Development',
  'Physical Development',
  'Relational Development',
  'Vocational Development',
  'Holistic Personal Growth',
  'Missional Development',
  'Emotional Development',
  'Mental Development'
];

export const DEVELOPMENT_TOPICS = [
  'Mental and Emotional Health',
  'Identity in Christ and Intrinsic Value',
  'Overcoming Temptation and Sin',
  'Moral and Ethical Decision-Making',
  'Personal Faith and Relationship with God',
  'Prayer and Spiritual Disciplines',
  'Understanding the Bible',
  'Spiritual Warfare',
  'Relationships and Community',
  'Marriage and Family',
  'Conflict Resolution',
  'Communication and Listening',
  'Work and Stewardship',
  'Time Management and Priorities',
  'Leadership and Servanthood',
  'Sexual Purity and Enjoyment',
  'Finances - Handling Money Gods Way',
  'Stewardship of the Body',
  'Evangelism and Sharing Your Faith',
  'Discipleship and Mentoring Others',
  'Cultrual Engagement and Worldview',
  'Service and Outreach',
  'Christian Character and Virtues',
  'Dealing with Trials and Suffering',
  'Fruit of the Spirit'
];

  